import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { SESSIONS_COLLECTION, firestore } from "../lib/firebase";
import type {
  AttendanceHalfDay,
  AttendanceRecord,
  AttendanceStatus,
} from "../types/access";
import { asRecord, toDate } from "../utils/firestoreData";
import { writeAuditEvent } from "./auditService";

const SESSION_ATTENDANCES_COLLECTION = "attendances";
const SESSION_ATTENDANCE_STATUSES_COLLECTION = "attendanceStatuses";

export type AttendanceWindow = {
  displayDate: string;
  halfDay: AttendanceHalfDay;
  idSuffix: string;
  label: string;
  sessionDate: string;
  timezone: string;
};

function sessionSubDocRef(
  sessionId: string,
  collectionName: string,
  documentId: string,
) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return doc(firestore, SESSIONS_COLLECTION, sessionId, collectionName, documentId);
}

function sessionSubCollectionRef(sessionId: string, collectionName: string) {
  if (!firestore) {
    throw new Error("Firebase n'est pas configuré.");
  }

  return collection(firestore, SESSIONS_COLLECTION, sessionId, collectionName);
}

function normalizeHalfDay(value: unknown): AttendanceHalfDay {
  return value === "afternoon" ? "afternoon" : "morning";
}

function normalizeAttendance(
  id: string,
  data: Record<string, unknown>,
): AttendanceRecord {
  return {
    createdAt: toDate(data.createdAt),
    halfDay: normalizeHalfDay(data.halfDay),
    id: typeof data.id === "string" ? data.id : id,
    participantSlotId:
      typeof data.participantSlotId === "string" ? data.participantSlotId : "",
    sessionDate: typeof data.sessionDate === "string" ? data.sessionDate : "",
    signatureSvg: typeof data.signatureSvg === "string" ? data.signatureSvg : "",
    timezone: typeof data.timezone === "string" ? data.timezone : "Europe/Paris",
    uid: typeof data.uid === "string" ? data.uid : "",
  };
}

function normalizeAttendanceStatus(
  id: string,
  data: Record<string, unknown>,
): AttendanceStatus {
  return {
    halfDay: normalizeHalfDay(data.halfDay),
    id: typeof data.id === "string" ? data.id : id,
    participantSlotId:
      typeof data.participantSlotId === "string" ? data.participantSlotId : "",
    sessionDate: typeof data.sessionDate === "string" ? data.sessionDate : "",
    status: "absent",
    updatedAt: toDate(data.updatedAt),
    updatedBy: typeof data.updatedBy === "string" ? data.updatedBy : "",
  };
}

function datePartsInTimeZone(value: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
    month: "2-digit",
    timeZone: timezone,
    year: "numeric",
  }).formatToParts(value);
  const byType = new Map(parts.map((part) => [part.type, part.value]));

  return {
    day: byType.get("day") ?? "01",
    hour: Number(byType.get("hour") ?? "0"),
    month: byType.get("month") ?? "01",
    year: byType.get("year") ?? "1970",
  };
}

export function buildAttendanceId({
  halfDay,
  participantSlotId,
  sessionDate,
}: {
  halfDay: AttendanceHalfDay;
  participantSlotId: string;
  sessionDate: string;
}): string {
  return `${participantSlotId}_${sessionDate}_${halfDay}`;
}

export function resolveAttendanceWindow({
  endAt,
  now = new Date(),
  startAt,
  timezone,
}: {
  endAt: Date | null;
  now?: Date;
  startAt: Date | null;
  timezone: string;
}): AttendanceWindow {
  const base =
    startAt && now < startAt ? startAt : endAt && now > endAt && startAt ? startAt : now;
  const parts = datePartsInTimeZone(base, timezone);
  const halfDay: AttendanceHalfDay = parts.hour < 12 ? "morning" : "afternoon";
  const sessionDate = `${parts.year}${parts.month}${parts.day}`;
  const label = halfDay === "morning" ? "Matin" : "Après-midi";

  return {
    displayDate: `${parts.day}/${parts.month}/${parts.year}`,
    halfDay,
    idSuffix: `${sessionDate}_${halfDay}`,
    label,
    sessionDate,
    timezone,
  };
}

export async function createParticipantAttendance({
  attendanceWindow,
  participantSlotId,
  sessionId,
  signatureSvg,
  uid,
}: {
  attendanceWindow: AttendanceWindow;
  participantSlotId: string;
  sessionId: string;
  signatureSvg: string;
  uid: string;
}): Promise<AttendanceRecord> {
  const attendanceId = buildAttendanceId({
    halfDay: attendanceWindow.halfDay,
    participantSlotId,
    sessionDate: attendanceWindow.sessionDate,
  });

  await setDoc(sessionSubDocRef(sessionId, SESSION_ATTENDANCES_COLLECTION, attendanceId), {
    createdAt: serverTimestamp(),
    halfDay: attendanceWindow.halfDay,
    id: attendanceId,
    participantSlotId,
    sessionDate: attendanceWindow.sessionDate,
    signatureSvg,
    timezone: attendanceWindow.timezone,
    uid,
  });

  const snapshot = await getDoc(
    sessionSubDocRef(sessionId, SESSION_ATTENDANCES_COLLECTION, attendanceId),
  );
  return normalizeAttendance(attendanceId, asRecord(snapshot.data()));
}

export async function readParticipantAttendance({
  attendanceId,
  sessionId,
}: {
  attendanceId: string;
  sessionId: string;
}): Promise<AttendanceRecord | null> {
  if (!firestore) {
    return null;
  }

  const snapshot = await getDoc(
    sessionSubDocRef(sessionId, SESSION_ATTENDANCES_COLLECTION, attendanceId),
  );

  return snapshot.exists()
    ? normalizeAttendance(snapshot.id, asRecord(snapshot.data()))
    : null;
}

export async function listSessionAttendances(
  sessionId: string,
): Promise<AttendanceRecord[]> {
  if (!firestore) {
    return [];
  }

  const snapshot = await getDocs(
    query(
      sessionSubCollectionRef(sessionId, SESSION_ATTENDANCES_COLLECTION),
      orderBy("createdAt", "desc"),
    ),
  );

  return snapshot.docs.map((attendanceDoc) =>
    normalizeAttendance(attendanceDoc.id, asRecord(attendanceDoc.data())),
  );
}

export async function listSessionAttendanceStatuses(
  sessionId: string,
): Promise<AttendanceStatus[]> {
  if (!firestore) {
    return [];
  }

  const snapshot = await getDocs(
    query(
      sessionSubCollectionRef(sessionId, SESSION_ATTENDANCE_STATUSES_COLLECTION),
      orderBy("updatedAt", "desc"),
    ),
  );

  return snapshot.docs.map((statusDoc) =>
    normalizeAttendanceStatus(statusDoc.id, asRecord(statusDoc.data())),
  );
}

export async function markParticipantAbsent({
  actorUid,
  attendanceWindow,
  participantSlotId,
  sessionId,
}: {
  actorUid: string;
  attendanceWindow: AttendanceWindow;
  participantSlotId: string;
  sessionId: string;
}): Promise<AttendanceStatus> {
  const statusId = buildAttendanceId({
    halfDay: attendanceWindow.halfDay,
    participantSlotId,
    sessionDate: attendanceWindow.sessionDate,
  });

  await setDoc(
    sessionSubDocRef(sessionId, SESSION_ATTENDANCE_STATUSES_COLLECTION, statusId),
    {
      halfDay: attendanceWindow.halfDay,
      id: statusId,
      participantSlotId,
      sessionDate: attendanceWindow.sessionDate,
      status: "absent",
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    },
  );
  await writeAuditEvent({
    actorUid,
    eventType: "attendance.absent.marked",
    metadata: {
      participantSlotId,
      sessionDate: attendanceWindow.sessionDate,
      sessionId,
    },
  });

  const snapshot = await getDoc(
    sessionSubDocRef(sessionId, SESSION_ATTENDANCE_STATUSES_COLLECTION, statusId),
  );
  return normalizeAttendanceStatus(statusId, asRecord(snapshot.data()));
}
